import { useMemo, useState } from "react"
import Select from "react-select"
import { Badge, Button, Col, Row, UncontrolledTooltip } from "reactstrap"

import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"
import TableContainer from "components/Common/TableContainer"
import { useListParams } from "hooks/useListParams"
import {
  useNotificationEventMeta,
  useNotificationEvents,
} from "hooks/notificationEventHook"
import NotificationEventModal from "./NotificationEventModal"

const NotificationEvents = () => {
  document.title = "Notification Events"

  const { params, updateParams, setPage, setSearch } = useListParams({
    page: 1,
    search: "",
  })
  const [editEvent, setEditEvent] = useState(null)

  const queryParams = useMemo(
    () => ({
      page: params.page ?? 1,
      per_page: 20,
      search: params.search,
      group: params.group,
    }),
    [params]
  )

  const { data: resp, isLoading, isRefetching } = useNotificationEvents(queryParams)
  const { data: metaResp } = useNotificationEventMeta()

  const rows = resp?.data?.data ?? []
  const paginationInfo = resp?.data
  const groups = metaResp?.data?.groups ?? []

  const groupLabel = useMemo(() => {
    const map = {}
    groups.forEach(g => {
      map[g.key] = g.label_en
    })
    return map
  }, [groups])

  const groupOptions = groups.map(g => ({ value: g.key, label: g.label_en }))
  const selectedGroup =
    groupOptions.find(o => o.value === params.group) ?? null

  const setGroup = option =>
    updateParams(prev => ({
      ...prev,
      group: option?.value ?? "",
      page: 1,
    }))

  const columns = useMemo(
    () => [
      {
        header: "Notification",
        accessorKey: "key",
        cell: ({ row }) => (
          <div>
            <div className="fw-medium">{row.original.title_en}</div>
            {row.original.title_bn && (
              <div className="fw-medium" lang="bn">
                {row.original.title_bn}
              </div>
            )}
            <small className="text-muted d-block">{row.original.body_en}</small>
            <code className="small">{row.original.key}</code>
          </div>
        ),
      },
      {
        header: "Category",
        cell: ({ row }) => (
          <Badge color="light" className="text-dark">
            {groupLabel[row.original.group] ?? row.original.group}
          </Badge>
        ),
      },
      {
        header: "Recipients",
        cell: ({ row }) => (
          <code className="small">{row.original.permission}</code>
        ),
      },
      {
        header: "Route",
        cell: ({ row }) => (
          <div>
            <code className="small">{row.original.route || "—"}</code>
            {row.original.deep_link && (
              <Badge color="info" className="ms-1">
                Direct
              </Badge>
            )}
          </div>
        ),
      },
      {
        header: "Status",
        cell: ({ row }) => (
          <div className="d-flex flex-column gap-1">
            <Badge color={row.original.active ? "success" : "secondary"}>
              {row.original.active ? "Active" : "Disabled"}
            </Badge>
            <Badge color="light" className="text-dark text-capitalize">
              {row.original.push_delivery}
            </Badge>
          </div>
        ),
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <div className="d-flex gap-1">
            <Button
              size="sm"
              color="primary"
              onClick={() => setEditEvent(row.original)}
              id={`edit-ne-${row.original.id}`}
            >
              <i className="mdi mdi-pencil-outline" />
            </Button>
            <UncontrolledTooltip target={`edit-ne-${row.original.id}`}>
              Edit
            </UncontrolledTooltip>
          </div>
        ),
      },
    ],
    [groupLabel]
  )

  return (
    <PageWrapper title="Notification Events" breadcrumbItem="Notification Events">
      <Row className="mb-3 align-items-center">
        <Col md={4}>
          <GlobalInputSearch
            value={params.search}
            onChange={setSearch}
            placeholder="Search notifications..."
          />
        </Col>
        <Col md={3}>
          <Select
            isClearable
            placeholder="All categories"
            value={selectedGroup}
            onChange={setGroup}
            options={groupOptions}
          />
        </Col>
        <Col>
          <p className="text-muted mb-0">
            Edit the wording, route and delivery of every system notification.
            Turning one off stops it for everyone; users can still opt out of a
            whole category from the mobile app.
          </p>
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={rows}
        isPagination
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
        className="table-responsive"
      />

      {editEvent && (
        <NotificationEventModal
          isOpen={Boolean(editEvent)}
          toggle={() => setEditEvent(null)}
          event={editEvent}
        />
      )}
    </PageWrapper>
  )
}

export default NotificationEvents
